import PersonAvatar from "@common-components/tasks/person-avatar/person-avatar";
import { IconButton, Menu } from "@mui/material";
import { selectCurrentWorkspace, selectUserObject } from "@common-reducers/UserSelectors";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import * as React from "react";
import SmallUserMenu from "./small-user-menu";
import styles from "./user-control-panel.module.scss";
import { BsThreeDots } from "react-icons/bs";
import { Box } from "@mui/system";
import { userDisplayName } from "@common-models/user";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

export interface UserControlPanelProps {
  isSideMenuOpen: boolean;
  readOnly?: boolean;
  /**
   * hides the user menu (three dots + avatar/name trigger) and only shows who
   * is signed in - used where the surrounding sidebar already lists the very
   * same destinations, so the popup would be a second, duplicate menu
   */
  hideMenu?: boolean;
}

const UserControlPanel = ({ isSideMenuOpen, readOnly = false, hideMenu = false }: UserControlPanelProps) => {
  const { t } = useTranslation(namespace);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  // the menu content (workspace row / upgrade item) can finish rendering after
  // the popover measured itself, leaving it positioned for a stale height:
  // MUI positions the paper by its top, so growing content pushes the bottom
  // down and away from the anchor. Re-position on every size change instead -
  // with transformOrigin bottom that keeps the bottom edge glued to the anchor
  // and lets the menu grow upwards.
  const menuActionRef = React.useRef<{ updatePosition: () => void } | null>(null);
  const paperRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const paper = paperRef.current;
    if (!open || hideMenu || !paper || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => menuActionRef.current?.updatePosition());
    observer.observe(paper);
    return () => observer.disconnect();
  }, [open, hideMenu]);
  const currentWorkspace = useAppSelector((state) => selectCurrentWorkspace(state));
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hideMenu) return;
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const direction = useAppSelector((state) => state.UserReducer.direction);

  const user = useAppSelector(selectUserObject);

  return (
    <div className={styles.mainUserContainer} style={{
      pointerEvents: readOnly ? "none" : "all",
      padding: isSideMenuOpen ? "16px" : "16px 11px",
      transition: 'padding 0.3s ease',
    }}>
      <Menu 
        sx={{
          "& .MuiPaper-elevation": {
            boxShadow: "0 5px 12px rgb(0 55 80 / 16%)",
            borderRadius: "10px !important",
            backgroundColor: "var(--color-surface)",
            color: "var(--color-text)",
            border: "1px solid var(--color-border)",
          }
        }}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open && !hideMenu}
        onClose={handleClose}
        // the control panel sits at the bottom of the side drawer, so the menu
        // must grow upwards from the anchor - with MUI's default (top/top) it
        // overflows the viewport and gets clamped to the bottom edge, which
        // makes it look detached from the avatar
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        action={menuActionRef as any}
        // keep the menu mounted so its workspace list is fetched at page load
        // instead of on the first open - otherwise the first open renders
        // without the workspace row, and the row popping in afterwards makes
        // the whole menu jump
        keepMounted={!hideMenu}
        TransitionProps={{
          onEntered: () => menuActionRef.current?.updatePosition(),
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{ ref: paperRef as any }}
      >
        <SmallUserMenu />
      </Menu>

      <div className={styles.user_container} style={{
        gap: isSideMenuOpen ? undefined : 0,
        transition: 'gap 0.3s ease',
      }}>
        <IconButton
          size="small"
          aria-label={t('common.displayMoreActions', 'Display more actions')}
          edge="end"
          color="inherit"
          className={styles.user_avatar}
          onClick={handleClick}
          disableRipple={hideMenu}
          sx={hideMenu ? { cursor: 'default' } : undefined}
        >
          <PersonAvatar
            size={44}
            email={user?.email}
            profileImg={user?.profileImg}
            lastName={user?.lastName}
            firstName={user?.firstName}
          />
        </IconButton>
        <span onClick={handleClick} className={styles.user_name} style={{
          cursor: hideMenu ? 'default' : undefined,
          overflow: 'hidden',
          maxWidth: isSideMenuOpen ? 200 : 0,
          opacity: isSideMenuOpen ? 1 : 0,
          transition: 'max-width 0.3s ease, opacity 0.2s ease',
        }}>
          {userDisplayName(user)}
          <span onClick={handleClick} className={styles.workspace_name}>
            {currentWorkspace?.name}
          </span>
        </span>
      </div>

      {!hideMenu && <Box sx={{
        marginInlineStart: isSideMenuOpen ? "auto" : 0,
        overflow: 'hidden',
        maxWidth: isSideMenuOpen ? 50 : 0,
        opacity: isSideMenuOpen ? 1 : 0,
        transition: 'max-width 0.3s ease, opacity 0.2s ease, margin 0.3s ease',
      }}>
        <button onClick={handleClick} className={styles.board_item_menu} data-testid="three-dots-button">
          <BsThreeDots size={35} color={"var(--color-primary)"} />
        </button>
      </Box>}
    </div>
  );
};
export default UserControlPanel;
